import * as Yup from 'yup';

import { YExpert } from 'components/DEBForm/ValidationStructure/YExpert';
import {
  ADDRESS_MESSAGE_FIELD_KEYS,
  COMPANY_FIELDS,
  GERMANY_COUNTRY_CODE,
} from 'components/FormHelper/AddressInput/addressInput.constants';

import { COUNTRY_OPTIONS, EIV_CHAR_LENGTH, VALIDATION_CONSTANTS } from 'global/constants';

import { getCanonicalFieldName, isDateField } from 'utils/FieldNameMapping';
import { YDateRequired, YPhoneNumber } from 'utils/YupUtils';

const ADDRESS_FIELD_KEYS = Object.values(COMPANY_FIELDS);
const PHONE_FIELD_KEYS = new Set(['mobile', 'phone', 'telefon']);
const EMAIL_FIELD_KEYS = new Set(['email', 'netzportalEmail', 'newEmail', 'confirmEmail']);

const YEmailRequired = Yup.string()
  .required(VALIDATION_CONSTANTS.MANDATORY)
  .email(VALIDATION_CONSTANTS.INVALID_EMAIL)
  .min(4, VALIDATION_CONSTANTS.TOO_SHORT)
  .max(EIV_CHAR_LENGTH.EMAIL, VALIDATION_CONSTANTS.TOO_LONG);

const prefixKey = (entityId, canonicalKey) => `${entityId}_${canonicalKey}`;

const isEmptyValue = (value) => value === undefined || value === null || value === '';

export const mapCountryNameToCode = (name) => {
  if (isEmptyValue(name)) {
    return '';
  }

  const normalized = String(name).trim();
  const found = COUNTRY_OPTIONS.find(
    (opt) =>
      opt.title === normalized ||
      opt.value === normalized ||
      opt.value.toUpperCase() === normalized.toUpperCase()
  );
  return found ? found.value : '';
};

const getEntityCountryFromContext = (context, entityId) =>
  mapCountryNameToCode(context?.parent?.[prefixKey(entityId, COMPANY_FIELDS.COUNTRY)]);

const remapValues = (sourceValues, entityId, fromPrefix) =>
  ADDRESS_FIELD_KEYS.reduce((acc, canonicalKey) => {
    const key = fromPrefix ? prefixKey(entityId, canonicalKey) : canonicalKey;
    acc[canonicalKey] = sourceValues?.[key] ?? '';
    return acc;
  }, {});

/**
 * Reuses a YDebFormSchema / YExpert field schema for format rules, but always
 * requires a value — task detail only renders fields that must be filled in.
 */
const requireWithBaseSchema = (baseSchema) =>
  Yup.mixed().test(
    'task-detail-required-field',
    VALIDATION_CONSTANTS.MANDATORY,
    function validateRequiredTaskDetailField(value) {
      if (isEmptyValue(value)) {
        return this.createError({ message: VALIDATION_CONSTANTS.MANDATORY });
      }

      try {
        baseSchema.validateSync(value, {
          abortEarly: true,
          parent: this.parent,
          context: this.options.context,
        });
        return true;
      } catch (err) {
        return this.createError({ message: err.message || VALIDATION_CONSTANTS.MANDATORY });
      }
    }
  );

export const isAddressMessageField = (field) =>
  ADDRESS_MESSAGE_FIELD_KEYS.has(getCanonicalFieldName(field.field));

/**
 * Same split as DebFormDataUtil.prepareDEBInitialData: digits before the first
 * non-digit → house number; the rest (trimmed) → addition.
 */
export const splitHouseNumberAndAddition = (rawHouseNumber) => {
  const raw = String(rawHouseNumber ?? '');
  const splitIndex = raw.search(/\D/);
  if (splitIndex === -1) {
    return { companyHouseNumber: raw, companyHouseNumberAddition: '' };
  }
  return {
    companyHouseNumber: raw.slice(0, splitIndex),
    companyHouseNumberAddition: raw.slice(splitIndex).trim(),
  };
};

/**
 * Inverse of splitHouseNumberAndAddition: rejoining must keep the `search(/\D/)`
 * boundary detectable so re-load still splits correctly.
 */
export const combineHouseNumberAndAddition = (houseNumber, addition) => {
  const number = String(houseNumber ?? '').trim();
  const add = String(addition ?? '').trim();
  if (!add) {
    return number;
  }
  if (!number) {
    return add;
  }
  // Addition already starts with a non-digit → direct concat ("12"+"a" → "12a").
  // Otherwise insert a space so search(/\D/) still finds the split ("12"+"3" → "12 3").
  return /^\D/.test(add) ? `${number}${add}` : `${number} ${add}`;
};

export const getTaskDetailAddressFieldSchema = (canonicalKey, debSchema, entityId) => {
  switch (canonicalKey) {
    case COMPANY_FIELDS.COUNTRY:
      return debSchema.fields[canonicalKey] ?? Yup.string().required(VALIDATION_CONSTANTS.MANDATORY);
    case COMPANY_FIELDS.HOUSE_NUMBER_ADDITION:
    case COMPANY_FIELDS.DISTRICT:
      return debSchema.fields[canonicalKey] ?? Yup.string().nullable().optional();
    case COMPANY_FIELDS.POSTAL_CODE:
      return Yup.lazy((value, context) => {
        if (getEntityCountryFromContext(context, entityId) === GERMANY_COUNTRY_CODE) {
          return Yup.string()
            .required(VALIDATION_CONSTANTS.MANDATORY)
            .min(5, VALIDATION_CONSTANTS.TOO_SHORT)
            .max(EIV_CHAR_LENGTH.BUERO_PLZ, VALIDATION_CONSTANTS.TOO_LONG);
        }
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(4, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_PLZ, VALIDATION_CONSTANTS.TOO_LONG);
      });
    case COMPANY_FIELDS.POSTAL_CITY_ID:
    case COMPANY_FIELDS.STREET_ID:
      return Yup.lazy((value, context) => {
        if (getEntityCountryFromContext(context, entityId) === GERMANY_COUNTRY_CODE) {
          return Yup.string().required(VALIDATION_CONSTANTS.MANDATORY);
        }
        return Yup.string().nullable().optional();
      });
    case COMPANY_FIELDS.DISTRICT_ID:
      return Yup.lazy((value, context) => {
        if (
          getEntityCountryFromContext(context, entityId) === GERMANY_COUNTRY_CODE &&
          value !== undefined &&
          value !== ''
        ) {
          return Yup.string().min(1, VALIDATION_CONSTANTS.TOO_SHORT);
        }
        return Yup.string().nullable().optional();
      });
    default:
      return (
        debSchema.fields[canonicalKey] ?? Yup.string().required(VALIDATION_CONSTANTS.MANDATORY)
      );
  }
};

export const getTaskDetailFieldSchema = (canonicalKey, fieldType, debSchema) => {
  if (ADDRESS_MESSAGE_FIELD_KEYS.has(canonicalKey)) {
    return getTaskDetailAddressFieldSchema(canonicalKey, debSchema);
  }

  if (fieldType === 'radio') {
    return Yup.mixed().test(
      'task-detail-radio-required',
      VALIDATION_CONSTANTS.MANDATORY,
      (value) => !isEmptyValue(value)
    );
  }

  if (fieldType === 'date' || isDateField(canonicalKey)) {
    return YDateRequired;
  }

  if (PHONE_FIELD_KEYS.has(canonicalKey)) {
    return YPhoneNumber.required(VALIDATION_CONSTANTS.MANDATORY);
  }

  if (EMAIL_FIELD_KEYS.has(canonicalKey)) {
    return YEmailRequired;
  }

  const baseSchema = debSchema.fields[canonicalKey] ?? YExpert.fields[canonicalKey];
  if (baseSchema) {
    return requireWithBaseSchema(baseSchema);
  }

  return Yup.string().required(VALIDATION_CONSTANTS.MANDATORY);
};

export const buildAddressValuesFromFields = (addressFields) => {
  const values = addressFields.reduce((acc, field) => {
    const canonicalKey = getCanonicalFieldName(field.field);
    acc[canonicalKey] = field.value ?? '';
    return acc;
  }, {});

  values[COMPANY_FIELDS.COUNTRY] = isEmptyValue(values[COMPANY_FIELDS.COUNTRY])
    ? ''
    : mapCountryNameToCode(values[COMPANY_FIELDS.COUNTRY]);

  // EIV stores addition inside bueroHausnummer; split when addition wasn't sent separately.
  if (!values[COMPANY_FIELDS.HOUSE_NUMBER_ADDITION] && values[COMPANY_FIELDS.HOUSE_NUMBER]) {
    const { companyHouseNumber, companyHouseNumberAddition } = splitHouseNumberAndAddition(
      values[COMPANY_FIELDS.HOUSE_NUMBER]
    );
    values[COMPANY_FIELDS.HOUSE_NUMBER] = companyHouseNumber;
    values[COMPANY_FIELDS.HOUSE_NUMBER_ADDITION] = companyHouseNumberAddition;
  }

  return values;
};

export const getAddressEntityIds = (fields) => {
  const entityIds = new Set();
  (fields ?? []).forEach((field) => {
    if (isAddressMessageField(field)) {
      entityIds.add(field.entityId);
    }
  });
  return entityIds;
};

export const shouldShowFieldError = (formik, fieldName) =>
  Boolean(formik?.errors?.[fieldName] && (formik?.touched?.[fieldName] || formik?.submitCount > 0));

export const createEntityScopedFormik = (formik, entityId) => {
  if (!formik || entityId == null) {
    return null;
  }

  return {
    values: remapValues(formik.values, entityId, true),
    initialValues: remapValues(formik.initialValues, entityId, true),
    errors: remapValues(formik.errors, entityId, true),
    touched: remapValues(formik.touched, entityId, true),
    submitCount: formik.submitCount,
    handleChange: (event) => {
      const fieldName = event?.target?.name;
      if (!fieldName) {
        formik.handleChange(event);
        return;
      }
      formik.setFieldValue(prefixKey(entityId, fieldName), event.target.value, false);
    },
    handleBlur: (event) => {
      const fieldName = event?.target?.name;
      if (!fieldName) {
        formik.handleBlur(event);
        return;
      }
      formik.setFieldTouched(prefixKey(entityId, fieldName), true, true);
    },
    setFieldValue: (name, value, shouldValidate = true) => {
      const prefixedName = prefixKey(entityId, name);
      formik.setFieldValue(prefixedName, value, shouldValidate);
      formik.setFieldTouched(prefixedName, true, false);
    },
    setFieldTouched: (name, touched = true, shouldValidate = false) => {
      formik.setFieldTouched(prefixKey(entityId, name), touched, shouldValidate);
    },
    resetForm: ({ values: canonicalValues } = {}) => {
      if (!canonicalValues) {
        formik.resetForm();
        return;
      }

      const prefixedValues = ADDRESS_FIELD_KEYS.reduce((acc, canonicalKey) => {
        if (canonicalKey in canonicalValues) {
          acc[prefixKey(entityId, canonicalKey)] = canonicalValues[canonicalKey];
        }
        return acc;
      }, {});

      formik.resetForm({
        values: {
          ...formik.values,
          ...prefixedValues,
        },
      });
    },
  };
};
